import * as Sentry from '@sentry/browser'

export const DEFAULT_REPLAY_PATH_PATTERN = ''

/**
 * Share of non-error sessions to record on replay pages, from 0 to 1.
 * Anything missing or invalid falls back to 0 (errors only).
 * @returns {number}
 */
export const getReplaySessionSampleRate = () => {
  // @ts-ignore
  const rate = parseFloat(
    import.meta.env.VITE_REPLAY_NO_ERROR_SESSION_SAMPLE_RATE,
  )

  if (Number.isNaN(rate)) {
    return 0
  }

  return Math.min(Math.max(rate, 0), 1)
}

/**
 * Whether this page should record a replay. The pattern is tested against the
 * path plus query string, so it can target e.g. `[?&]flow=foo(&|$)`.
 * @returns {boolean}
 */
export const shouldRecordReplay = () => {
  // @ts-ignore
  const pattern =
    import.meta.env.VITE_REPLAY_PATH_PATTERN || DEFAULT_REPLAY_PATH_PATTERN

  if (!pattern) {
    return false
  }

  let matcher
  try {
    matcher = new RegExp(pattern, 'i')
  } catch {
    return false
  }

  return matcher.test(window.location.pathname + window.location.search)
}

/**
 * Load Sentry
 * @param {string} dsn - Sentry DSN
 * @param {Partial<import('@sentry/browser').BrowserOptions>} [overrides] - Optional Sentry.init overrides
 */
export const loadSentry = (dsn, overrides = {}) => {
  try {
    const recordReplay = shouldRecordReplay()

    Sentry.init({
      dsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        ...(recordReplay ? [Sentry.replayIntegration()] : []),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
      ],
      beforeSend: (event) => {
        if (
          // @ts-ignore
          typeof window.disableSentry === 'function' &&
          // @ts-ignore
          window.disableSentry() === true
        ) {
          return null
        }
        return event
      },
      // Tracing
      tracesSampleRate: 0.5, //  Capture 50% of the transactions
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/trbotrkr\.com\//,
        // blog.turbodevhub.com (Strapi) rejects sentry-trace/baggage in CORS
        // preflight, which breaks the offers fetch on /thanks
        /^https:\/\/(?!blog\.)(?:.+\.)?turbodevhub\.com\//,
      ],
      // Session Replay: only on pages matching the pattern above
      replaysSessionSampleRate: getReplaySessionSampleRate(),
      replaysOnErrorSampleRate: 1.0,
      ...overrides,
    })

    // grab the user trace to pass to HotJar
    const sentryUserTrace = Sentry.withScope((scope) => {
      const userTraceId = scope.getScopeData().propagationContext.traceId
      return userTraceId || null
    })

    // @ts-ignore
    window.sentryUserTraceId = sentryUserTrace
  } catch (error) {
    console.warn('Sentry failed to initialize', error)
  }
}
